// Pure normalisation helpers for waypoints, the counterpart to highlightDetails
// for the shared detail-sheet components.
//
// These live here rather than inline in the dialog components for the same
// reason as the highlight versions: the mappings are deterministic, they are the
// part most likely to break silently when the API shape shifts, and in a
// computed property they cannot be unit-tested.

export interface WaypointPhoto {
  key: string;
  thumbUrl: string;
  fullUrl: string;
}

// Photos for the strip, each carrying both the grid thumbnail and the full-size
// URL for the lightbox.
//
// NOTE: an entry with no usable thumbnail is dropped entirely. `mdUrl` is the
// only thumbnail source consulted, so a file carrying just `lgUrl` disappears
// from the strip — the highlight equivalent falls back to `lgUrl` instead. That
// asymmetry is preserved here rather than quietly "fixed"; see the spec.
export function waypointPhotos(waypoint: any): WaypointPhoto[] {
  if (!waypoint) return [];

  return (waypoint.waypointPhotos ?? [])
    .map((photo: any, index: number) => {
      const file = photo.file ?? {};
      return {
        key: String(photo.id ?? photo.clientId ?? `photo-${index}`),
        thumbUrl: file.mdUrl || file.url || '',
        fullUrl: file.lgUrl || file.url || '',
      };
    })
    .filter((photo: WaypointPhoto) => photo.thumbUrl.length > 0);
}

// A waypoint group can list the same waypoint more than once; the dialog shows
// each one only once, keeping the first occurrence so the group's own ordering
// survives.
export function uniqueWaypointsById(waypoints: any): any[] {
  if (!Array.isArray(waypoints)) return [];

  const seenIds = new Set();
  return waypoints.filter((waypoint: any) => {
    if (seenIds.has(waypoint?.id)) return false;

    seenIds.add(waypoint?.id);
    return true;
  });
}

export function waypointCountLabel(count: number): string {
  return count === 1 ? '1 waypoint' : `${count} waypoints`;
}
